import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    CheckCircle2,
    Download,
    Mail,
    Home as HomeIcon,
    Compass,
    Sparkles,
    AlertTriangle,
} from "lucide-react";
import SEO from "@/components/common/SEO";
import ShareBar from "@/components/common/ShareBar";
import { api } from "@/lib/api";
import { track } from "@/lib/tracker";
import { SITE_URL } from "@/lib/api";
import { LIBRO } from "@/data/staticContent";

export default function Gracias() {
    const [params] = useSearchParams();
    const orderId =
        params.get("order_id") ||
        params.get("external_reference") ||
        params.get("preference_id");
    const status = (params.get("status") || params.get("collection_status") || "approved").toLowerCase();

    const [order, setOrder] = useState(null);

    useEffect(() => {
        if (!orderId) return;
        api.get(`/checkout/orders/${orderId}`)
            .then(({ data }) => {
                setOrder(data);
                if ((data?.status || "").toLowerCase() === "paid") {
                    track("order_completed_view", {
                        order_id: orderId,
                        amount: data?.amount,
                    });
                }
            })
            .catch(() => {});
    }, [orderId]);

    const ok = status === "approved" || status === "paid" || status === "free" || (order?.status === "free");

    return (
        <>
            <SEO
                title={ok ? "¡Gracias! · El Principio del Mapa Inverso" : "Tu orden · El Principio del Mapa Inverso"}
                description="Confirmación de tu descarga del libro El Principio del Mapa Inverso."
                path="/gracias"
            />

            <section className="py-16 md:py-24" data-testid="gracias-section">
                <div className="container-x max-w-3xl">
                    <div className="dossier">
                        <div className="dossier-header">
                            <span>EMI · Confirmación de descarga</span>
                            <span className={ok ? "text-[var(--warning)]" : "text-[var(--danger)]"}>
                                {ok ? "Descarga lista" : "Pendiente"}
                            </span>
                        </div>
                        <div className="p-8 md:p-12">
                            <div className="text-center mb-10">
                                {ok ? (
                                    <CheckCircle2 className="w-16 h-16 text-[var(--warning)] mx-auto mb-4" />
                                ) : (
                                    <AlertTriangle className="w-16 h-16 text-[var(--danger)] mx-auto mb-4" />
                                )}
                                <p className="eyebrow justify-center mb-3">
                                    {ok ? "Operación 01 confirmada" : "Estado pendiente"}
                                </p>
                                <h1 className="font-display text-4xl md:text-5xl leading-[0.95] mb-4">
                                    {ok ? (
                                        <>
                                            Gracias por leer.{" "}
                                            <span className="text-[var(--danger)]">De verdad.</span>
                                        </>
                                    ) : (
                                        <>
                                            Tu descarga está{" "}
                                            <span className="text-[var(--danger)]">en proceso</span>.
                                        </>
                                    )}
                                </h1>
                                <p className="font-mono text-sm text-[var(--paper)] leading-relaxed max-w-xl mx-auto">
                                    {ok
                                        ? (order?.download_url
                                            ? "Te enviamos el PDF y EPUB del libro a tu correo. Si no llega en 10 minutos, revisa la carpeta de spam o usa el botón de descarga acá abajo."
                                            : "Te enviamos el PDF y EPUB del libro a tu correo. Si no llega en 10 minutos, revisa la carpeta de spam o escríbenos.")
                                        : "Estamos preparando tu descarga. En unos segundos deberías recibir el correo."}
                                </p>
                            </div>

                            <div className="border border-dashed border-[var(--ink-dim)] bg-[#0d0d0d] p-5 mb-8">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-3">
                                    Detalles de la descarga
                                </p>
                                <dl className="space-y-2 font-mono text-xs">
                                    <Row k="Libro" v={LIBRO.title} />
                                    <Row k="Autor" v={LIBRO.author} />
                                    <Row k="Formato" v={LIBRO.format} />
                                    <Row k="Precio" v="Gratis" />
                                    {orderId && <Row k="Orden" v={orderId} />}
                                    {order?.buyer?.email && <Row k="Email" v={order.buyer.email} />}
                                    {order?.status && <Row k="Estado" v={order.status} highlight />}
                                </dl>
                            </div>

                            <div className="flex flex-wrap gap-3 justify-center mb-10">
                                {ok && order?.download_url && (
                                    <a
                                        href={order.download_url}
                                        className="btn-brutal btn-brutal-danger"
                                        data-testid="gracias-download"
                                        onClick={() => track("book_download_clicked", { order_id: orderId })}
                                    >
                                        <Download className="w-4 h-4" />
                                        Descargar PDF
                                    </a>
                                )}
                                <a
                                    href={`mailto:contacto@elmapainverso.com`}
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="gracias-contact"
                                >
                                    <Mail className="w-4 h-4" />
                                    Escribir si algo falla
                                </a>
                                <Link
                                    to="/mapa-inverso"
                                    className="btn-brutal"
                                    data-testid="gracias-mapa"
                                >
                                    <Compass className="w-4 h-4" />
                                    Ir a la app del Mapa Inverso
                                </Link>
                                <Link
                                    to="/"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="gracias-home"
                                >
                                    <HomeIcon className="w-4 h-4" />
                                    Volver al inicio
                                </Link>
                            </div>

                            {ok && (
                                <div className="border-l-2 border-[var(--warning)] pl-6 py-3 mb-10">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-2 flex items-center gap-2">
                                        <Sparkles className="w-3 h-3" />
                                        Recomendación
                                    </p>
                                    <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                        Léelo lento. Cada capítulo tiene un{" "}
                                        <strong className="text-[var(--paper-2)]">Mapa Inverso</strong>{" "}
                                        al final. No los saltes. Y si en algún
                                        momento te sobrepasa, deja el libro y
                                        usa la app: ahí lo conviertes en plan.
                                    </p>
                                </div>
                            )}

                            {ok && (
                                <ShareBar
                                    url={`${SITE_URL}/`}
                                    title="El Principio del Mapa Inverso"
                                    text="Acabo de leer un libro que necesitaba hace años: 'El Principio del Mapa Inverso' de Nelson González. Si conoces a alguien que lo necesita, pásaselo:"
                                    hashtags={["MapaInverso", "Sobriedad", "NiTanAdicto"]}
                                    source="gracias_page"
                                    variant="dark"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

function Row({ k, v, highlight }) {
    return (
        <div className="flex justify-between gap-3 py-1 border-b border-dashed border-[var(--ink-dim)] last:border-0">
            <dt className="text-[var(--ink-muted)] uppercase tracking-[0.15em]">{k}</dt>
            <dd className={`text-right break-all ${highlight ? "text-[var(--warning)] font-bold" : "text-[var(--paper-2)]"}`}>
                {v}
            </dd>
        </div>
    );
}
